import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { ListBandHeader } from '../ui/ListBandHeader.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Pressable } from '../ui/Pressable.js';

export type TimeBand = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'older_2_3_months' | 'older';

const BAND_ORDER: TimeBand[] = ['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older'];

const BAND_I18N_KEYS: Record<TimeBand, string> = {
  today: 'common.timeBands.today',
  yesterday: 'common.timeBands.yesterday',
  this_week: 'common.timeBands.thisWeek',
  this_month: 'common.timeBands.thisMonth',
  older_2_3_months: 'common.timeBands.older2_3Months',
  older: 'common.timeBands.older',
};

export function getTimeBand(isoTimestamp: string): TimeBand {
  const now = Date.now();
  const DAY = 86_400_000;
  const ts = new Date(isoTimestamp.endsWith('Z') ? isoTimestamp : isoTimestamp + 'Z').getTime();
  const ms = now - ts;
  if (ms < DAY) return 'today';
  if (ms < 2 * DAY) return 'yesterday';
  if (ms < 7 * DAY) return 'this_week';
  if (ms < 30 * DAY) return 'this_month';
  if (ms < 90 * DAY) return 'older_2_3_months';
  return 'older';
}

interface BandGroup<T> {
  band: TimeBand;
  items: T[];
}

interface TimeBandedListProps<T, Id extends number | string = number> {
  items: T[];
  getTimestamp: (item: T) => string;
  getId: (item: T) => Id;
  renderItem: (item: T) => React.ReactNode;
  isUnread?: (item: T) => boolean;
  checkedIds: Set<Id>;
  /**
   * El cotejo del divisor: selecciona o suelta todas las filas de la banda.
   * Sin esto el divisor NO muestra cotejo. Antes era obligatorio, y seis
   * listas pasaban `() => {}`: el cotejo se veía y no hacía nada (Eduardo,
   * 2026-10-12). Lo normal es `(ids, todos) => ids.forEach(id => alternar(id, !todos))`.
   */
  onBandCheck?: (ids: Id[], allChecked: boolean) => void;
  /** If provided, bands in this set are collapsed */
  collapsedBands?: Set<string>;
  onToggleBand?: (band: string) => void;
  /**
   * Estilo de log: el divisor sin fondo ni bordes, y las filas separadas con
   * su propia caja — el de Mensajería.
   *
   * El otro estilo (banda con fondo y filete arriba y abajo) es el de las
   * listas que llenan la pantalla, donde la banda corta de lado a lado. En una
   * lista adentro de una tarjeta, esa banda parece un encabezado de tabla
   * pegado a filas redondeadas, y no cierra.
   */
  plain?: boolean;
  /** Sentinel element rendered after the last item (for infinite scroll) */
  loadMoreSentinel?: React.ReactNode;
  emptyState?: React.ReactNode;
}

export function TimeBandedList<T, Id extends number | string = number>({
  items,
  getTimestamp,
  getId,
  renderItem,
  isUnread,
  checkedIds,
  onBandCheck,
  collapsedBands: externalCollapsed,
  onToggleBand: externalToggle,
  plain = false,
  loadMoreSentinel,
  emptyState,
}: TimeBandedListProps<T, Id>) {
  const { t } = useTranslation();
  const [internalCollapsed, setInternalCollapsed] = useState<Set<string>>(new Set());

  const collapsed = externalCollapsed ?? internalCollapsed;
  const toggle = externalToggle ?? ((band: string) => {
    setInternalCollapsed(prev => {
      const next = new Set(prev);
      next.has(band) ? next.delete(band) : next.add(band);
      return next;
    });
  });

  const groups = useMemo<BandGroup<T>[]>(() => {
    const map = new Map<TimeBand, T[]>();
    for (const item of items) {
      const band = getTimeBand(getTimestamp(item));
      if (!map.has(band)) map.set(band, []);
      map.get(band)!.push(item);
    }
    return BAND_ORDER.filter(b => map.has(b)).map(b => ({ band: b, items: map.get(b)! }));
  }, [items, getTimestamp]);

  if (items.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <>
      {groups.map(({ band, items: bandItems }) => {
        const isCollapsed = collapsed.has(band);
        const ids = bandItems.map(getId);
        const checkedInBand = ids.filter(id => checkedIds.has(id));
        const allChecked = checkedInBand.length === ids.length && ids.length > 0;
        const someChecked = checkedInBand.length > 0 && !allChecked;
        const unreadCount = isUnread ? bandItems.filter(isUnread).length : 0;

        return (
          <React.Fragment key={band}>
            {/* Band header — componente único del sistema (ListBandHeader).
                Antes cada lista escribía su propia banda y quedaban distintas
                entre módulos. */}
            <ListBandHeader
              {...(plain ? { plain: true } : {})}
              label={t(BAND_I18N_KEYS[band])}
              count={bandItems.length}
              collapsed={isCollapsed}
              onToggle={() => toggle(band)}
              checked={allChecked}
              indeterminate={someChecked}
              {...(onBandCheck ? { onCheckChange: () => onBandCheck(ids, allChecked) } : {})}
              {...(unreadCount > 0 ? {
                right: (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 'var(--evo-badge-sq)', height: 'var(--evo-badge-sq)',
                    borderRadius: '50%', fontSize: 'var(--evo-fs-caption)', fontWeight: 700,
                    flexShrink: 0, background: 'var(--evo-cyan)', color: 'var(--evo-fg-on-cyan)',
                    textTransform: 'none', letterSpacing: 0, boxSizing: 'border-box',
                  }}>
                    {unreadCount}
                  </span>
                ),
              } : {})}
            />
            {/* Band items — las FILAS. El refactor a ListBandHeader se las
                había llevado por delante: quedaban las cabeceras y ninguna
                fila, en todas las listas de la app a la vez. */}
            <div
              style={{
                overflow: 'hidden',
                transition: 'max-height 200ms ease-out',
                maxHeight: isCollapsed ? 0 : '100000px',
              }}
            >
              {bandItems.map(item => (
                <React.Fragment key={getId(item)}>
                  {renderItem(item)}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        );
      })}
      {loadMoreSentinel}
    </>
  );
}
